import { useEffect, useRef } from 'react';
import { entityResearch, refreshEntityResearch, relmapEntity, relmapGraph, relmapSearch, verdictHistory, type RelGraph, type ResearchEntityType } from '@aithora/shared';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { EmptyState, ErrorState, Skeleton } from '../../ui/states';
import type { TabProps } from './common';
import { mountAtlasNetwork, setData, type EngineHandle } from './relmap/engine.js';

const ENTITY_TYPES: readonly ResearchEntityType[] = ['operator', 'building', 'payer', 'program', 'partner'];
export const asEntityType = (t: string): ResearchEntityType => ((ENTITY_TYPES as readonly string[]).includes(t) ? (t as ResearchEntityType) : 'partner');

/**
 * Atlas Intelligence > Market > Relationship Map (Console V4: aiIsRelMap T15233, the
 * atlas-network component). Who is connected to whom around one entity: the graph, the
 * Channels view (Suppliers and Referral sources), Territory (Texas counties by annual flow),
 * the Browse rail over the whole catalog, each entity's profile, and web research with the
 * Atlas verdict. The drawing is the design's own engine (relmap/engine.js); every entity,
 * link and research answer comes from the back end (GET /intelligence/relmap, /search,
 * /research, /verdicts). Sales sees research but never the verdict, and no Deal Desk.
 */
export function RelMapTab(p: TabProps) {
  const { client, sales } = p;
  const hostRef = useRef<HTMLDivElement | null>(null);
  const handle = useRef<EngineHandle | null>(null);
  const graph = useQuery<RelGraph>((signal) => relmapGraph(client, { signal }), [client]);
  // the latest props for the engine's callbacks (the engine mounts once per network)
  const live = useRef(p);
  live.current = p;

  useEffect(() => {
    const box = hostRef.current;
    if (!graph.data || !box) return;
    const host = document.createElement('atlas-network');
    host.setAttribute('viewer-role', sales ? 'Sales' : 'Operator');
    box.appendChild(host);
    setData(graph.data);
    const L = () => live.current;
    handle.current = mountAtlasNetwork(host, {
      viewerRole: sales ? 'Sales' : 'Operator',
      research: {
        get: (type, id) => entityResearch(L().client, { params: { entityType: asEntityType(type), id } }).then((r) => r.research),
        history: (id) => (L().sales ? Promise.resolve([]) : verdictHistory(L().client, { params: { id } }).then((r) => r.history)),
        refresh: (type, id, onStep) => refreshEntityResearch(L().client, asEntityType(type), id, onStep),
      },
      search: (q, group) => relmapSearch(L().client, { query: { q, group, limit: 40 } }).then((r) => r.hits),
      // Every facility the operator owns, 200 a page (the server's PROFILE_PAGE).
      allBuildings: (operatorId, page) =>
        relmapEntity(L().client, { params: { id: operatorId }, query: { page } }).then((r) => ({
          rows: r.lists.buildings ?? [],
          more: r.more === true,
          note: r.notes.find((x) => /in the catalog under this operator/.test(x)) ?? '',
        })),
      loadFocal: (id, openIt) => {
        relmapGraph(L().client, { query: { focal: id } }).then((d) => handle.current?.load(d, openIt !== false), () => undefined);
      },
      onOpenDeal: sales
        ? undefined
        : (d) =>
            L().go('northstar', {
              nsView: 'Deal Desk',
              nsDealFrom: d.name,
              nsDealSrc: { id: d.id, name: d.name, beds: d.beds, occ: d.occ, trend: d.trend, role: d.role, payers: d.payers, referrals: d.referrals },
            }),
    });
    return () => {
      handle.current?.destroy();
      handle.current = null;
      host.remove();
    };
    // the role is fixed for the session; a new network remounts the engine
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph.data]);

  // An empty catalog is a state, not a failure (the server answers 404 until operators load).
  if (graph.error && !graph.data && graph.error.status === 404) {
    return <EmptyState title="No operators in the catalog yet" body="The map draws once the facility catalog has operators loaded. Find still searches what is there." />;
  }
  if (graph.error && !graph.data) return <ErrorState error={graph.error} onRetry={graph.reload} />;
  if (!graph.data) return <Skeleton rows={8} label="Loading the Relationship Map" />;
  return <div ref={hostRef} data-testid="relmap" style={sx('height:100%;')} />;
}
